import React, { createContext, Dispatch, FC, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as RD from 'fp-ts-remote-data';
import * as TE from 'fp-ts/TaskEither';
import * as T from 'fp-ts/Task';
import * as M from 'fp-ts/Map';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import * as String from 'fp-ts/string';
import { HttpError, HttpRemoteData, HttpTask } from '@core/http';
import { PrismicGalleryContent, PrismicTagContent, PrismicVisitContent } from '@modules/visits/model';
import { constVoid, pipe } from 'fp-ts/function';

import * as Prismic from '@prismicio/client';

import { getAll, getPrismicDocumentByUID, PrismicContent, PrismicDocument } from '@core/prismic';
import { renderHttpRemoteData } from '@shared/utils/render';
import Seo from '@shared/components/seo/Seo';
import { sequenceS } from 'fp-ts/Apply';
import { Language } from '@shared/modules/translation';
import { useCurrentLanguage } from '@shared/modules/translation/hooks';
import { getAccountTypeCookie } from '@shared/utils/cookies';
import { getConfigValue } from '@shared/utils/config';

interface VisitContextValue {
  visit: PrismicDocument<PrismicVisitContent>;
  tags: Map<string, PrismicDocument<PrismicTagContent>>;
  galleries: Map<string, PrismicDocument<PrismicGalleryContent>>;
  setTags: Dispatch<SetStateAction<Map<string, PrismicDocument<PrismicTagContent>>>>;
  setGalleries: Dispatch<SetStateAction<Map<string, PrismicDocument<PrismicGalleryContent>>>>;
}

const VisitContext = createContext<VisitContextValue>({
  visit: {} as any,
  tags: new Map<string, PrismicDocument<PrismicTagContent>>(),
  galleries: new Map<string, PrismicDocument<PrismicGalleryContent>>(),
  setTags: constVoid,
  setGalleries: constVoid,
});

function getAllToMap<R extends PrismicContent>(
  task: HttpTask<Array<PrismicDocument<R>>>,
): HttpTask<Map<string, PrismicDocument<R>>> {
  return pipe(
    task,
    TE.map(documents => new Map<string, PrismicDocument<R>>(documents.map(document => [document.id, document]))),
  );
}

export function fetchVisit(id: string, lang: Language) {
  return pipe(
    TE.Do,
    TE.bind('visit', () => getPrismicDocumentByUID<PrismicVisitContent>('visit_app', id, lang)),
    TE.bind('related', ({ visit }) =>
      pipe(
        sequenceS(TE.ApplyPar)({
          tags: getAllToMap(
            getAll<PrismicTagContent>(lang, {
              predicates: [
                Prismic.predicate.at('document.type', 'tag_app'),
                Prismic.predicate.at('my.tag_app.visit', visit.id),
              ],
            }),
          ),
          galleries: getAllToMap(
            getAll<PrismicGalleryContent>(lang, {
              predicates: [
                Prismic.predicate.at('document.type', 'gallery_app'),
                Prismic.predicate.at('my.gallery_app.visit', visit.id),
              ],
            }),
          ),
        }),
      ),
    ),
  );
}

type VisitResult = {
  visit: PrismicDocument<PrismicVisitContent>;
  related: {
    tags: Map<string, PrismicDocument<PrismicTagContent>>;
    galleries: Map<string, PrismicDocument<PrismicGalleryContent>>;
  };
};

function updateVisitState(
  setVisit: Dispatch<SetStateAction<HttpRemoteData<PrismicDocument<PrismicVisitContent>>>>,
  setTags: Dispatch<SetStateAction<Map<string, PrismicDocument<PrismicTagContent>>>>,
  setGalleries: Dispatch<SetStateAction<Map<string, PrismicDocument<PrismicGalleryContent>>>>,
) {
  return (result: VisitResult) => {
    setVisit(RD.success(result.visit));
    setTags(result.related.tags);
    setGalleries(result.related.galleries);
  };
}


const VisitContextProvider: FC = ({ children }) => {
  const { id } = useParams<'id'>() as { id: string };

  const language = useCurrentLanguage();

  const [visit, setVisit] = useState<HttpRemoteData<PrismicDocument<PrismicVisitContent>>>(() => RD.pending);

  const [tags, setTags] = useState<Map<string, PrismicDocument<PrismicTagContent>>>(
    () => new Map<string, PrismicDocument<PrismicTagContent>>(),
  );

  const [galleries, setGalleries] = useState<Map<string, PrismicDocument<PrismicGalleryContent>>>(
    () => new Map<string, PrismicDocument<PrismicGalleryContent>>(),
  );

  const transformedId = transformIdForAccountType(id);

  useEffect(() => {
    setVisit(RD.pending);

    const updateState = updateVisitState(setVisit, setTags, setGalleries);

    pipe(
      fetchVisit(transformedId, language),
      TE.fold(
        err => T.fromIO(() => setVisit(RD.failure(err))),
        res => T.fromIO(() => updateState(res)),
      ),
    )();
  }, [language, transformedId, id]);

  return renderHttpRemoteData(visit, visit => (
    <VisitContext.Provider value={{ visit, tags, galleries, setTags, setGalleries }}>
      <Seo title={visit.data.name} />
      {children}
    </VisitContext.Provider>
  ));
};

export function useVisitContext() {
  return useContext(VisitContext);
}

export function transformIdForAccountType(id: string, explicitAccountType?: 'scholar' | 'standard' | null): string {
  const configValue = getConfigValue('VITE_USING_ACCOUNT_TYPES');
  const isAccountTypeEnabled = pipe(
    configValue,
    O.map(value => value.toLowerCase() === 'true'),
    O.getOrElse(() => false),
  );

  if (!isAccountTypeEnabled) {
    return id;
  }

  const accountType = explicitAccountType !== undefined ? explicitAccountType : getAccountTypeCookie();
  const scholarPrefix = 'scholar-';
  const hasScholarPrefix = id.startsWith(scholarPrefix);
  
  if (accountType === 'scholar') {
    return hasScholarPrefix ? id : `${scholarPrefix}${id}`;
  } else {
    return hasScholarPrefix ? id.substring(scholarPrefix.length) : id;
  }
}

export function useGallery(id: string) {
  const { galleries } = useVisitContext();
  
  const transformedId = transformIdForAccountType(id);

  return useMemo(
    () =>
      pipe(
        Array.from(galleries.values()),
        A.findFirst(gallery => gallery.uid === transformedId),
        RD.fromOption(() => HttpError.notFound),
      ),
    [galleries, transformedId],
  );
}

export function useTag(id: string) {
  const { tags } = useVisitContext();
  
  const transformedId = transformIdForAccountType(id);

  return useMemo(
    () =>
      pipe(
        O.Do,
        O.bind('tag', () =>
          pipe(
            Array.from(tags.values()),
            A.findFirst(tag => tag.uid === transformedId),
          ),
        ),
        O.bind('related', ({ tag }) =>
          pipe(
            tag.data.related_tags.map(tag => tag.tag),
            A.filterMap(O.fromNullable),
            A.filterMap(tag => M.lookup(String.Eq)(tag.id)(tags)),
            O.some,
          ),
        ),
        RD.fromOption(() => HttpError.notFound),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, tags, transformedId],
  );
}

export default VisitContextProvider;
